import { useState } from "react";
import { toast } from "sonner";
import { Bookmark, Loader2, Mail, X, Check } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const EMAIL_KEY = "pb_saved_email";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Save-recipe CTA. Two visual modes:
 *   variant="icon"  → circular 40px icon button (for use on top-right of cards)
 *   variant="pill"  → full pill button with label (for detail pages / sidebars)
 *
 * On click, opens a modal that collects the parent's email (prefilled from
 * localStorage) and POSTs to `/api/recipes/{id}/email`. On success, shows a
 * toast and remembers the email for future sessions.
 */
export default function SaveRecipeButton({ recipe, variant = "icon", className = "" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(EMAIL_KEY) || "";
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const openModal = (e) => {
    // On cards the button sits inside a <Link>; we don't want the row click to fire.
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSent(false);
    setOpen(true);
    trackEvent("recipe_save_open", { recipe_id: recipe?.id, recipe_title: recipe?.title });
  };

  const closeModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const cleaned = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleaned)) {
      toast.error("That email doesn't look right — try again?");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.id}/email`, { email: cleaned });
      window.localStorage.setItem(EMAIL_KEY, cleaned);
      setSent(true);
      trackEvent("recipe_saved", { recipe_id: recipe.id, recipe_title: recipe.title });
      toast.success(data?.message || "Recipe sent to your inbox!");
      // Auto-close after a beat so the user sees the check
      setTimeout(() => setOpen(false), 1600);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't send that recipe. Try again in a moment?";
      toast.error(String(msg));
    } finally {
      setSending(false);
    }
  };

  const IconBtn = (
    <button
      type="button"
      data-testid={`btn-save-recipe-${recipe?.id}`}
      onClick={openModal}
      aria-label={`Save ${recipe?.title || "recipe"} to email`}
      className={`h-10 w-10 rounded-full bg-white/95 backdrop-blur shadow-md flex items-center justify-center hover:bg-pb-coral hover:text-white text-pb-ink transition-all duration-200 active:scale-95 ${className}`}
    >
      <Bookmark className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );

  const PillBtn = (
    <button
      type="button"
      data-testid={`btn-save-recipe-${recipe?.id}`}
      onClick={openModal}
      className={`inline-flex items-center gap-2 px-5 py-3 rounded-full bg-pb-ink text-white font-black text-sm shadow-lg hover:bg-pb-coral hover:shadow-xl transition-all duration-200 active:scale-[0.98] ${className}`}
    >
      <Mail className="h-4 w-4" />
      Email me this recipe
    </button>
  );

  return (
    <>
      {variant === "pill" ? PillBtn : IconBtn}
      {open && (
        <div
          data-testid="save-recipe-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="absolute inset-0 bg-pb-ink/60 backdrop-blur-sm animate-fade-in"
            aria-hidden="true"
          />
          <div
            className="relative bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              data-testid="btn-save-recipe-close"
              aria-label="Close"
              className="absolute top-4 right-4 h-8 w-8 rounded-full text-pb-soft hover:bg-pb-cream flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>

            {sent ? (
              <div className="text-center py-6">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                  <Check className="h-7 w-7" strokeWidth={3} />
                </div>
                <h3 className="font-display font-black text-2xl text-pb-ink">On its way!</h3>
                <p className="mt-2 text-pb-soft text-sm">
                  Check your inbox in a minute — it's in there ready for the grocery run.
                </p>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pb-coral/10 text-pb-coral text-[11px] font-black uppercase tracking-widest">
                  <Bookmark className="h-3 w-3" /> Save Recipe
                </div>
                <h3 className="font-display font-black text-2xl text-pb-ink mt-3 leading-tight">
                  Email {recipe?.title ? <span className="text-pb-coral">{recipe.title}</span> : "this recipe"} to yourself
                </h3>
                <p className="mt-2 text-pb-soft text-sm leading-relaxed">
                  Ingredients + steps land in your inbox — perfect for the grocery run or a fridge print-out.
                </p>

                <form onSubmit={submit} className="mt-5 space-y-3">
                  <label htmlFor="save-recipe-email" className="block">
                    <span className="text-[11px] font-black uppercase tracking-widest text-pb-soft">
                      Your email
                    </span>
                    <input
                      id="save-recipe-email"
                      data-testid="input-save-recipe-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoFocus
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-pb-cream bg-pb-cream focus:border-pb-coral focus:bg-white outline-none transition font-semibold text-pb-ink placeholder:text-pb-soft/60"
                    />
                  </label>

                  <button
                    type="submit"
                    data-testid="btn-save-recipe-submit"
                    disabled={sending}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-pb-coral text-white font-black shadow-lg hover:bg-pb-ink transition-colors disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" /> Send it to my inbox
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-pb-soft text-center">
                    One recipe. No spam. Unsubscribe anytime.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
